/**
 * As caixas de MARCAR das abas da ficha: o grupo com título e a caixa isolada com apoio.
 *
 * POR QUE ESTAS PEÇAS EXISTEM FORA DO CHASSI, e isto é pendência declarada: `@complexity/ui` não
 * tem caixa de marcação (medido em `packages/ui/src/index.ts`, 19/09/2026). A `SelecaoMultipla`
 * que ele oferece é uma lista suspensa com etiquetas, e o legado mostra as opções ABERTAS, cada uma
 * marcada com um toque (`admin.html:2383-2439`, `5197-5208`, `5149-5162`). Trocar por lista
 * suspensa mudaria o gesto, e o alvo da migração é o gesto igual. A escolha de UMA opção já existe
 * em `src/comum/grupo-radio.tsx` (A5) e não se repete aqui.
 *
 * Servem às abas da A2 e da A7 (Coloração e Milhões usam as duas), e ficam nesta pasta até o
 * principal decidir subi-las para `src/comum/`.
 */

// O identificador estável da caixa isolada, para o rótulo apontar para ela.
import { useId, type ReactNode } from 'react';

/** Uma opção de marcar: o valor gravado e o texto exibido, iguais no legado. */
export interface OpcaoDeMarcacao {
  readonly valor: string;
  readonly rotulo: string;
}

// A linha de cada opção: o alvo de toque de 44px vale para a linha inteira, e não só para a caixa.
const LINHA = 'flex min-h-toque cursor-pointer items-center gap-cx-2 text-cx-sm text-texto';
// A caixa em si, na cor da marca: `accent-color` pinta a caixa nativa com o token, nos dois temas.
const CAIXA = 'h-cx-4 w-cx-4 shrink-0 accent-marca';

/**
 * Um grupo de caixas de marcar com título, como o `.checkgroup[data-group]` do legado.
 *
 * `emLinha` põe as opções lado a lado com quebra, como as etapas da Coloração
 * (`flex-direction:row; flex-wrap:wrap; gap:14px`, `admin.html:5197`).
 */
export function GrupoDeMarcacao({
  titulo,
  complemento,
  opcoes,
  marcadas,
  aoMudar,
  emLinha = false,
  desabilitado = false,
  ocultarTitulo = false,
}: {
  readonly titulo: string;
  // O "(marque quantas quiser)" do legado, em letra menor ao lado do título.
  readonly complemento?: string;
  readonly opcoes: readonly OpcaoDeMarcacao[];
  // Decide se cada opção aparece marcada; recebe o valor da opção.
  readonly marcadas: (valor: string) => boolean;
  // Chamado com o valor e o novo estado da caixa.
  readonly aoMudar: (valor: string, marcada: boolean) => void;
  readonly emLinha?: boolean;
  readonly desabilitado?: boolean;
  // Esconde o título da tela quando um cabeçalho visível já nomeia o grupo; o leitor de tela o ouve.
  readonly ocultarTitulo?: boolean;
}) {
  return (
    // `fieldset` com `legend` dá ao grupo o nome que o leitor de tela anuncia antes das opções.
    <fieldset className="m-0 flex min-w-0 flex-col gap-cx-1 border-0 p-0" disabled={desabilitado}>
      <legend className={ocultarTitulo ? 'cx-so-leitor' : 'mb-cx-1 p-0 text-cx-sm font-medium text-texto'}>
        {titulo}
        {complemento && <span className="ml-cx-1 text-cx-xs font-normal text-texto-suave">{complemento}</span>}
      </legend>
      <div className={emLinha ? 'flex flex-wrap gap-x-cx-4' : 'flex flex-col'}>
        {opcoes.map((opcao) => (
          <label key={opcao.valor} className={LINHA}>
            <input
              type="checkbox"
              className={CAIXA}
              value={opcao.valor}
              checked={marcadas(opcao.valor)}
              onChange={(e) => aoMudar(opcao.valor, e.target.checked)}
            />
            {opcao.rotulo}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Uma caixa de marcar sozinha, com rótulo e uma linha de apoio embaixo, como cada item do
 * Checklist de Milhões (`admin.html:5155-5159`): o elemento é o rótulo, o efeito é o apoio.
 */
export function CaixaDeMarcacao({
  rotulo,
  apoio,
  marcada,
  aoMudar,
  desabilitado = false,
}: {
  readonly rotulo: string;
  readonly apoio?: ReactNode;
  readonly marcada: boolean;
  readonly aoMudar: (marcada: boolean) => void;
  readonly desabilitado?: boolean;
}) {
  const id = useId();
  return (
    <div className="flex items-start gap-cx-2 py-cx-1">
      {/* A caixa fica no alto, alinhada à primeira linha do rótulo, como no legado. */}
      <input
        id={id}
        type="checkbox"
        className={`${CAIXA} mt-cx-1`}
        checked={marcada}
        disabled={desabilitado}
        onChange={(e) => aoMudar(e.target.checked)}
      />
      <div className="min-w-0">
        {/* O rótulo também marca: tocar no nome é o alvo maior. */}
        <label htmlFor={id} className="cursor-pointer text-cx-sm font-medium text-texto">
          {rotulo}
        </label>
        {apoio && <div className="text-cx-xs text-texto-suave">{apoio}</div>}
      </div>
    </div>
  );
}

/** Transforma uma lista de textos em opções cujo valor e rótulo são o mesmo texto, como no legado. */
export function opcoesDe(textos: readonly string[]): OpcaoDeMarcacao[] {
  return textos.map((t) => ({ valor: t, rotulo: t }));
}

/**
 * Liga e desliga um valor numa lista de marcados, mantendo a ORDEM DAS OPÇÕES na tela.
 *
 * O legado grava as marcadas na ordem em que aparecem no formulário (`querySelectorAll(':checked')`,
 * `admin.html:4876`), e não na ordem em que foram clicadas. Por isso a lista nova é refeita a
 * partir da ordem das opções.
 */
export function alternarMarcado(
  marcados: readonly string[],
  opcoes: readonly string[],
  valor: string,
  marcado: boolean,
): string[] {
  // O conjunto depois do clique.
  const conjunto = new Set(marcados);
  if (marcado) conjunto.add(valor);
  else conjunto.delete(valor);
  // Só as opções da tela, na ordem da tela.
  return opcoes.filter((o) => conjunto.has(o));
}
